import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import Header from 'quill/formats/header';
import React, { useEffect, useMemo, useState } from 'react';
import HeaderId from '../editor/formats/HeaderId';
import DelayedRenderBlockGroup from '../editor/formats/namespaces/postPreview/DelayedRenderBlockGroup';
import QuillComponent from '../editor/QuillComponent';
import type QuillComponentProps from '../editor/QuillComponentProps';
import { useQuill, withQuillContextProvider } from '../editor/QuillContext';
import type { TableDocument } from '../editor/table/types';
import type { RenderBlock } from '../editor/utils/deltaToRenderBlocks';
import deltaToRenderBlocks, {
  renderBlocksToDelta,
} from '../editor/utils/deltaToRenderBlocks';
import { PostPreviewControlsContext } from '../editor/PostPreviewControls';
import type { PostPreviewControlsOptions } from '../editor/PostPreviewControls';

const SCROLL_MARGIN = 10;
const MAX_COMPLEXITY_PER_GROUP = 1000;

const findScrollingContainer = (element: HTMLElement) => {
  let current: HTMLElement | null = element;
  while (current) {
    const overflow = getComputedStyle(current).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') {
      return current;
    }

    current = current.parentElement;
  }

  return null;
};

const computeComplexity = (block: RenderBlock) => {
  const attributeComplexity = Object.keys(block.attributes).length * 2;
  if (block.type === 'text') {
    return block.line.reduce(
      (sum, op) =>
        sum + (op.attributes ? Object.keys(block.attributes).length : 0),
      attributeComplexity,
    );
  }

  let embedComplexity = 1;
  if (block.key === 'table-embed') {
    const table = block.value as TableDocument;
    embedComplexity = new Delta(table.rows).length();
  } else if (block.key === 'code-embed') {
    embedComplexity = 10;
  }

  return attributeComplexity + embedComplexity;
};

const chunkByComplexity = (renderBlocks: RenderBlock[]) => {
  const groups: RenderBlock[][] = [];

  let currentComplexity = 0;
  let currentGroup: RenderBlock[] = [];
  renderBlocks.forEach((renderBlock, index) => {
    currentComplexity += computeComplexity(renderBlock);
    currentGroup.push(renderBlock);

    if (
      currentComplexity > MAX_COMPLEXITY_PER_GROUP ||
      index === renderBlocks.length - 1
    ) {
      groups.push(currentGroup);
      currentGroup = [];
      currentComplexity = 0;
    }
  });

  return groups;
};

interface PostPreviewQuillComponentProps
  extends Omit<
    QuillComponentProps,
    'readOnly' | 'namespace' | 'content' | 'initialContent' | 'quillRef'
  > {
  content: Delta | Op[];
  anchor?: string;
  namespace?: 'postPreview' | 'embedPostContent';
  postUrl?: string;
}

const PostPreviewQuillComponent = ({
  content,
  anchor: initialAnchor,
  rootClassName,
  namespace = 'postPreview',
  postUrl,
  ...props
}: PostPreviewQuillComponentProps) => {
  const { quill } = useQuill();

  // We store the anchor as an object to be able to trigger
  // the useEffect if the anchor changes even though the value
  // is the same, which can happen when you click a link multiple times
  const [anchor, setAnchor] = useState<{
    current: string | undefined;
  }>({ current: initialAnchor });

  const delta = useMemo(() => {
    const [titleBlock, ...resetBlocks] = deltaToRenderBlocks(
      new Delta(content),
    );
    if (!titleBlock) return new Delta().insert('\n');

    const renderBlockGroups = chunkByComplexity(resetBlocks);
    const anchorHeaderId = anchor.current?.split('-')[0];

    const anchorGroup =
      anchorHeaderId &&
      renderBlockGroups.find((renderBlocks) =>
        renderBlocks.some(
          ({ attributes }) => attributes['header-id'] === anchorHeaderId,
        ),
      );

    return renderBlockGroups.reduce((acc, group, index) => {
      if (anchorGroup ? group === anchorGroup : index === 0) {
        return acc.concat(renderBlocksToDelta(group));
      }
      acc.insert({ [DelayedRenderBlockGroup.blotName]: group });
      return acc;
    }, renderBlocksToDelta([titleBlock]));
  }, [content, anchor]);

  useEffect(() => {
    const anchorHeaderId = anchor.current?.split('-')[0];

    if (!anchorHeaderId || !quill) return undefined;
    const header = quill.scroll
      .descendants(Header)
      .find((h) => HeaderId.value(h.domNode) === anchorHeaderId);

    const scrollingContainer = header && findScrollingContainer(header.domNode);
    if (!scrollingContainer) return undefined;

    const scrollToAnchor = () => {
      // Element#scrollIntoView() does not work due to CSS transform scaling.
      const containerTop = scrollingContainer.getBoundingClientRect().top;
      const selfTop = header.domNode.getBoundingClientRect().top;

      scrollingContainer.scrollTop += selfTop - containerTop - SCROLL_MARGIN;
    };

    const observer = new ResizeObserver(scrollToAnchor);
    observer.observe(scrollingContainer);
    return () => {
      observer.disconnect();
    };
  }, [content, anchor, quill]);

  const postPreviewControls = useMemo((): PostPreviewControlsOptions => {
    return {
      scrollToAnchor(hash) {
        setAnchor({ current: hash.slice(1) });
      },
      postUrl,
    };
  }, [postUrl]);

  return (
    <PostPreviewControlsContext.Provider value={postPreviewControls}>
      <QuillComponent
        content={delta}
        rootClassName={rootClassName}
        contentPlaceholder=""
        namespace={namespace}
        readOnly
        smallTitleSize
        titlePlaceholder="Untitled"
        moduleConfig={{
          postPreviewControls: postPreviewControls as unknown as Record<
            string,
            unknown
          >,
        }}
        {...props}
      />
    </PostPreviewControlsContext.Provider>
  );
};

export default withQuillContextProvider(PostPreviewQuillComponent);
